'use client';

import React, { useEffect, useState } from 'react';
import { Box, VStack } from '@skin-seoul/design-system';
import ProductBasicInfo from './components/ProductBasicInfo';
import ProductOptionsSelection from './components/ProductOptionsSelection';
import ProductQuantitySelector from './components/ProductQuantitySelector';
import ProductActionButtons from './components/ProductActionButtons';
import ProductBenefits from './components/ProductBenefits';
import type { ProductDetail, SimpleProductDetail, VariationProductDetail } from '@skin-seoul/service';
import { RECENTLY_VIEWED_PRODUCTS_KEY, useRegionCurrency, ProductType, useCartService, useProductService } from '@skin-seoul/service';
import Cookies from 'js-cookie';
import { useSafeRouter } from '@/hooks';
import { useCartDrawer } from '@skin-seoul/shared-components';
import { useParams } from 'next/navigation';
import BMSMCampaignBannerSection from './BMSMCampaignBannerSection';
import { usePageLogger } from '@/utils/logger';
import { getDeviceId } from '@/utils/deviceId';
interface ProductBuySectionProps {
  initialSelectedOption: string;
}
interface AddToCartGAParams {
  product: ProductDetail;
  selectedOptionProduct: SimpleProductDetail | VariationProductDetail;
  quantity: number;
  currency: string;
}
const generateAddToCartGAParameters = ({
  product,
  selectedOptionProduct,
  quantity,
  currency
}: AddToCartGAParams) => {
  const itemPrice = selectedOptionProduct.salePrice ?? selectedOptionProduct.originalPrice;
  const totalPrice = (itemPrice * quantity).toString();
  const items = product.type === ProductType.Simple ? [{
    item_id: product.productId.toString(),
    item_name: product.name,
    item_brand: product.brand || '',
    item_sku: product.sku,
    price: (product.salePrice ?? product.originalPrice).toString(),
    item_category: product.category?.[0] || '',
    item_category2: product.category?.[1] || ''
  }] : product.variations.map(variation => ({
    item_id: variation.productId.toString(),
    item_name: variation.name,
    item_brand: variation.brand || '',
    item_sku: variation.sku,
    price: (variation.salePrice ?? variation.originalPrice).toString(),
    item_category: variation.category?.[0] || '',
    item_category2: variation.category?.[1] || ''
  }));
  return {
    'x-fb-cd-content_type': 'product',
    'x-fb-cd-content_name': selectedOptionProduct.name,
    'x-fb-cd-content_ids': selectedOptionProduct.productId.toString(),
    value: totalPrice,
    currency: currency,
    items: items,
    event_name: 'add_to_cart',
    device_id: getDeviceId(),
    event_time: Math.round(new Date().getTime() / 1000),
    item_variant: `${selectedOptionProduct.size}(${selectedOptionProduct.pack})`,
    product_sku: selectedOptionProduct.sku
  };
};
const ProductBuySection = ({
  initialSelectedOption
}: ProductBuySectionProps) => {
  const {
    useGetProductsDetail
  } = useProductService();
  const logger = usePageLogger();
  const {
    currency
  } = useRegionCurrency();
  const params = useParams();
  const {
    data: {
      product
    }
  } = useGetProductsDetail({
    slug: params.slug as string,
    currency
  });
  const {
    mutate: addToCart,
    status
  } = useCartService().useAddToCart({
    currency
  });
  const {
    onCartOpen
  } = useCartDrawer();
  const router = useSafeRouter();
  const initialSelectedOptionProduct = (() => {
    if (product.type === ProductType.Simple) {
      return product;
    }
    return product.variations.find(variation => variation.productId === initialSelectedOption);
  })();
  const [selectedOption, setSelectedOption] = useState<string | undefined>(initialSelectedOptionProduct?.status !== 'outofstock' ? initialSelectedOptionProduct?.productId : undefined);
  const [quantity, setQuantity] = useState(1);
  const selectedOptionProduct = (() => {
    if (product.type === ProductType.Simple) {
      return product;
    }
    return product.variations.find(variation => variation.productId === selectedOption);
  })();
  const handleQuantityChange = (increment: boolean) => {
    if (increment) {
      setQuantity(prev => Math.min(prev + 1, 999));
    } else {
      setQuantity(prev => Math.max(prev - 1, 1));
    }
  };
  const handleAddToBag = () => {
    if (!selectedOptionProduct) {
      console.error('selectedOptionProduct is not found');
      return;
    }
    const addToCartGAParams = generateAddToCartGAParameters({
      product,
      selectedOptionProduct,
      quantity,
      currency
    });
    logger?.click('add_to_cart', addToCartGAParams);
    addToCart({
      sku: selectedOptionProduct.sku,
      quantity,
      currency
    });
    onCartOpen();
  };
  const handleBuyNow = () => {
    if (!selectedOptionProduct) {
      console.error('selectedOptionProduct is not found');
      return;
    }
    logger?.click('click_buy_now', {
      quantity,
      product_sku: selectedOptionProduct.sku,
      product_price: selectedOptionProduct.salePrice ?? selectedOptionProduct.originalPrice,
      item_variant: `${selectedOptionProduct.size}(${selectedOptionProduct.pack})`,
      product_name: selectedOptionProduct.name
    });
    router.push(`/checkout/?add-to-cart=${selectedOption}&quantity=${quantity}`);
  };
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: product.name,
          text: `${product.brand} - ${product.name}`,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch (error) {
      console.error('share error', error);
    }
  };
  const handleWishlistToggle = (action: 'add' | 'remove') => {
    if (!selectedOptionProduct) {
      return;
    }
    const itemPrice = selectedOptionProduct.salePrice ?? selectedOptionProduct.originalPrice;
    const itemVariant = `${selectedOptionProduct.size}(${selectedOptionProduct.pack})`;
    logger?.click('wishlist_click', {
      action,
      x_fb_cd_content_type: 'product',
      x_fb_cd_content_name: product.name,
      x_fb_cd_content_ids: product.productId,
      value: itemPrice.toString(),
      currency,
      items: [{
        item_id: product.productId,
        item_name: product.name,
        item_brand: product.brand || '',
        item_sku: product.sku,
        price: itemPrice.toString(),
        item_category: product.category?.[0] || '',
        item_category2: product.category?.[1] || '',
        item_variant: itemVariant,
        product_image_url: product.productImageUrls?.[0] || ''
      }],
      item_list_name: 'PDP - Wishlist',
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      page_type: 'pdp',
      event_time: Date.now()
    });
  };
  useEffect(() => {
    if (!product) {
      return;
    }
    const recentlyViewedProducts = Cookies.get(RECENTLY_VIEWED_PRODUCTS_KEY) || '';
    const recentlyViewedProductsArray = recentlyViewedProducts.split('|').filter(Boolean);
    const productIdStr = product.productId.toString();
    const filteredArray = recentlyViewedProductsArray.filter(id => id !== productIdStr);
    filteredArray.unshift(productIdStr);
    Cookies.set(RECENTLY_VIEWED_PRODUCTS_KEY, filteredArray.join('|'));
  }, [product]);
  return <VStack width="100%" gap={0} data-sentry-element="VStack" data-sentry-component="ProductBuySection" data-sentry-source-file="ProductBuySection.tsx">
      <ProductBasicInfo onShare={handleShare} selectedOption={selectedOption ?? initialSelectedOption} data-sentry-element="ProductBasicInfo" data-sentry-source-file="ProductBuySection.tsx" />
      {product.status !== 'outofstock' && <BMSMCampaignBannerSection />}
      {product.type === ProductType.Variable && <ProductOptionsSelection product={product} selectedOption={selectedOption} onOptionChange={setSelectedOption} />}

      <Box width="100%" background="white" paddingY={{
      base: 3,
      tablet: 6
    }} paddingX={{
      base: 4,
      tablet: 4
    }} data-sentry-element="Box" data-sentry-source-file="ProductBuySection.tsx">
        <VStack gap={4} width="100%" alignItems="flex-start" data-sentry-element="VStack" data-sentry-source-file="ProductBuySection.tsx">
          <ProductQuantitySelector quantity={quantity} onQuantityChange={handleQuantityChange} data-sentry-element="ProductQuantitySelector" data-sentry-source-file="ProductBuySection.tsx" />
          <ProductActionButtons onAddToBag={handleAddToBag} onBuyNow={handleBuyNow} sku={product?.sku} productStatus={product.status} addToBagStatus={status} hasSelectedOption={Boolean(selectedOptionProduct)} onWishlistToggle={handleWishlistToggle} data-sentry-element="ProductActionButtons" data-sentry-source-file="ProductBuySection.tsx" />
        </VStack>
      </Box>

      <ProductBenefits data-sentry-element="ProductBenefits" data-sentry-source-file="ProductBuySection.tsx" />
    </VStack>;
};
export default ProductBuySection;